"use client";

import type { FC } from "react";
import { useMemo } from "react";

import {
  StoreLocatorGetDirection,
  StoreLocatorShopSchedule,
} from "@locators/components/shared/components/StoreLocatorCard/components";
import { getProductDisplay } from "@locators/components/shared/utils/shopProductTypes";
import { getServiceDisplay } from "@locators/components/shared/utils/shopServices";
import { getStoreTypeDisplay } from "@locators/components/shared/utils/storeTypeName";
import type { StoreDetailsComponentProps } from "@locators/components/StoreDetails";
import { ServiceIconTextPair } from "@locators/components/StoreDetails/components/StoreDetailsV2/components/ServiceIconTextPair";
import { ServicesGrid } from "@locators/components/StoreDetails/components/StoreDetailsV2/components/ServicesGrid";
import { StoreDetailsCard } from "@locators/components/StoreDetails/components/StoreDetailsV2/components/StoreDetailsCard";
import { StoreDetailsProductsByBrand } from "@locators/components/StoreDetails/components/StoreDetailsV2/components/StoreDetailsProductsByBrand";
import {
  StoreLocatorContext,
  StoreLocatorMap,
  StoreLocatorProviderWrapper,
  useDirectionURL,
  useStoreLocator,
  useStoreTrackingEvent,
  useStoreVisibility,
} from "@locators/components/StoreLocator";

import { useConfig } from "@jti/app-context/useConfig";
import { ChevronBackwardIcon } from "@jti/brand-icons/chevron-backward";
import { SelectionTileItem } from "@jti/core-components";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { useNearbyStores } from "../../hooks/useNearbyStores";
import { StoreDetailsNearbyShops } from "../StoreDetailsNearbyShops";

import styles from "./StoreDetailsV2.module.scss";

export const StoreDetailsV2: FC<StoreDetailsComponentProps> = ({
  storeDetails,
  storeIcons,
  storeLocatorUrl,
  componentLevelLanguage,
  storeDetailsPageUrl,
}) => {
  const storeDetailsPage = useMemo(
    () => storeDetailsPageUrl || (typeof window === "undefined" ? "" : window.location.pathname),
    [storeDetailsPageUrl]
  );
  const storeLocatorData = useStoreLocator({
    ...storeDetails,
    defaultLat: String(storeDetails.store.Location.lat || 0),
    defaultLng: String(storeDetails.store.Location.lon || 0),
    defaultZoom: 15,
    activeStore: storeDetails.store,
    storeDetailsPage,
    storeIcons,
  });

  const { __ } = useTranslations();
  const { locationServiceMapProvider } = useConfig();
  const publishStoreTrackingEvent = useStoreTrackingEvent();

  const { visibility, processedData } = useStoreVisibility(storeDetails);
  const { availableProducts, availableProductsVendingMachine, productAttributes, availableServices } = processedData;

  const directionURL = useDirectionURL({
    store: storeDetails.store,
    locationServiceMapProvider: locationServiceMapProvider,
  });

  const onGetDirectionClick = () => {
    const store = storeDetails.store;
    publishStoreTrackingEvent(store.id, store.StoreName, store.storetype ?? "");
  };

  const { nearbyStores } = useNearbyStores({
    store: storeDetails?.store,
    isEnabled: storeDetails?.isNearbyShopsEnabled,
  });

  return (
    <StoreLocatorProviderWrapper componentLevelLanguage={componentLevelLanguage}>
      <StoreLocatorContext.Provider value={storeLocatorData}>
        <div className={styles.storeDetailsV2Container}>
          <div className={styles.storeDetailsV2GridWrapper}>
            {!!storeLocatorUrl?.length && (
              <LinkBehaviour href={storeLocatorUrl} className={styles.storeDetailsBackButton}>
                <ChevronBackwardIcon width="14px" height="14px" />
                <span>{__("store_locator.store_details.back_button")}</span>
              </LinkBehaviour>
            )}
            <div className={styles.storeDetailsGrid}>
              <div className={styles.storeDetailsTitle}>{storeDetails.store.StoreName}</div>
              <div className={styles.storeDetailsInfoColumn}>
                {visibility.isGeneralAnnouncementVisible && (
                  <AlertBanner tone="info" description={storeDetails.store.opening_hours?.[0]?.general_announcement} />
                )}

                {visibility.isStoreTypeVisible && (
                  <p className={styles.storeDetailsStoreName}>
                    {__(getStoreTypeDisplay(storeDetails.store.storetype ?? "")?.i18nKey ?? "")}
                  </p>
                )}

                <p className={styles.storeDetailsStoreInfo}>
                  {(storeDetails.store.address || storeDetails.store.Country) && (
                    <span>{[storeDetails.store.address, storeDetails.store.Country].filter(Boolean).join(", ")}</span>
                  )}
                  {storeDetails.store.PhoneNumber && <span>{storeDetails.store.PhoneNumber}</span>}
                </p>

                {directionURL && <StoreLocatorGetDirection href={directionURL} onClick={onGetDirectionClick} />}

                {visibility.isOpeningHoursVisible ? (
                  <StoreDetailsCard title={__("store_locator.store_details.opening_hours")}>
                    {storeDetails.store.opening_hours?.[0] && (
                      <StoreLocatorShopSchedule
                        shopSchedule={storeDetails.store.opening_hours[0]}
                        isListView={false}
                        isFoldable={true}
                        forceOpeningHoursBreaks={storeDetails?.isOpeningHoursBreaksEnabled || false}
                      />
                    )}
                  </StoreDetailsCard>
                ) : (
                  storeDetails.store?.Hours && (
                    <StoreDetailsCard title={__("store_locator.store_details.opening_hours")}>
                      <div>
                        <span className={styles.hours}>{__("store_locator.popup.hours_prefix")}</span>{" "}
                        {storeDetails.store?.Hours}
                      </div>
                    </StoreDetailsCard>
                  )
                )}

                {visibility.isAvailableProductsVisible && (
                  <StoreDetailsCard title={__("store_locator.store_details.available_products")}>
                    <div className={styles.storeDetailsCardsTiles}>
                      {availableProducts
                        .map((product) => {
                          const display = getProductDisplay(product.key);
                          if (!display) return null;

                          return (
                            <SelectionTileItem
                              key={product.key}
                              value={product.key}
                              name="available-products"
                              label={__(display.i18nKey)}
                              readOnly
                              icon={<div className={styles.storeDetailsIconContainer}>{display.icon}</div>}
                            />
                          );
                        })
                        .filter(Boolean)}
                    </div>
                  </StoreDetailsCard>
                )}

                {visibility.isAvailableSticksVisible && (
                  <StoreDetailsProductsByBrand
                    title={__("store_locator.store_details.available_stick_flavours")}
                    evoAttribute={productAttributes.productFlavours ?? undefined}
                    lyoAttribute={productAttributes.productFlavoursLYO ?? undefined}
                    isGroupedByBrand={visibility.isMultiBrandSticksVisible}
                    groupName="product-flavours"
                  />
                )}

                {visibility.isVendingMachineVisible && (
                  <StoreDetailsProductsByBrand
                    title={__("store_locator.store_details.vending_machine_availability")}
                    evoAttribute={productAttributes.productFlavoursVendingMachine ?? undefined}
                    lyoAttribute={productAttributes.productFlavoursLYOVendingMachine ?? undefined}
                    isGroupedByBrand={visibility.isMultiBrandSticksVisible}
                    isVendingMachineTipEnabled={storeDetails.isVendingMachineTipEnabled}
                    additionalProducts={availableProductsVendingMachine}
                    additionalProductsIconContainerClassName={styles.storeDetailsIconContainer}
                    groupName="vending-machine-products"
                  />
                )}
              </div>

              <div className={styles.storeDetailsMap}>
                <StoreLocatorMap />
              </div>
            </div>
          </div>

          {visibility.isAvailableServicesVisible && (
            <div className={styles.storeDetailsServicesWrapper}>
              <h1>{__("store_locator.store_details.services_available")}</h1>
              <ServicesGrid>
                {availableServices
                  .map((service) => {
                    const display = getServiceDisplay(service.key);
                    if (!display) return null;

                    return (
                      <ServiceIconTextPair
                        key={service.key}
                        icon={<div className={styles.storeDetailsServiceIcon}>{display.icon}</div>}
                        title={__(display.i18nKey)}
                      />
                    );
                  })
                  .filter(Boolean)}
              </ServicesGrid>
            </div>
          )}

          {visibility.isNearbyShopsEnabled && (
            <div className={styles.nearbyShops}>
              <StoreDetailsNearbyShops
                stores={nearbyStores}
                storeDetailsPage={storeDetailsPage}
                defaultZoom={storeDetails.defaultZoom}
                isOpeningHoursEnabled={storeDetails.isOpeningHoursEnabled}
                isOpeningHoursBreaksEnabled={storeDetails.isOpeningHoursBreaksEnabled}
                isAvailableDevicesEnabled={storeDetails.isAvailableDevicesEnabled}
                isAvailableSticksEnabled={storeDetails.isAvailableSticksEnabled}
                isMultiBrandSticksEnabled={storeDetails.isMultiBrandSticksEnabled}
                isAvailableServicesEnabled={storeDetails.isAvailableServicesEnabled}
                isNearbyShopsEnabled={storeDetails.isNearbyShopsEnabled}
                isGeneralAnnouncementEnabled={storeDetails.isGeneralAnnouncementEnabled}
                isStoreTypeEnabled={storeDetails.isStoreTypeEnabled}
                isVendingMachineEnabled={storeDetails.isVendingMachineEnabled}
                isVendingMachineTipEnabled={storeDetails.isVendingMachineTipEnabled}
              />
            </div>
          )}
        </div>
      </StoreLocatorContext.Provider>
    </StoreLocatorProviderWrapper>
  );
};
